import { NavLink, Outlet, Link, useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, BookOpen, LifeBuoy, CalendarDays, Briefcase, Users, User, LogOut, ArrowUpRight, ClipboardList, GraduationCap, BarChart3, MapPin, Inbox, BrainCircuit, BookMarked, Sparkles, Mail, FolderKanban, Menu, X, Film, BookText,
} from "lucide-react";
import { useCampusAuth } from "@/contexts/CampusAuth";
import OnboardingWizard from "@/components/campus/OnboardingWizard";

// Sidebar-Gruppen (Februar 2026): inhaltliche Bündelung statt 19 lose Einträge.
const GROUP_HAUPT = {
  label: "Mein Bereich",
  items: [
    { to: "/campus/app",         label: "Dashboard",        icon: LayoutDashboard, end: true },
    { to: "/campus/app/pflege-ki", label: "Pflege-KI",      icon: BrainCircuit },
    { to: "/campus/app/patientenmappe", label: "Patientenmappen", icon: FolderKanban },
    { to: "/campus/app/profil",  label: "Profil",           icon: User },
  ],
};

const GROUP_INHALTE = {
  label: "Inhalte",
  items: [
    { to: "/campus/app/schatzkiste",                 label: "Schatzkiste",         icon: BookOpen },
    { to: "/campus/app/pflege-ki/wissensbibliothek", label: "Wissensbibliothek",   icon: BookMarked },
    { to: "/campus/app/qpr-lexikon",                 label: "QPR-Lexikon",         icon: BookText },
    { to: "/campus/app/briefings",                   label: "Briefings",           icon: CalendarDays },
    { to: "/campus/app/lagezentrum",                 label: "#mdk-lagezentrum",    icon: LifeBuoy },
  ],
};

const GROUP_KARRIERE = {
  label: "Karriere",
  items: [
    { to: "/campus/app/jobs",              label: "Offene Stellen",       icon: Briefcase },
    { to: "/campus/app/talents",           label: "Jobsuche",             icon: Users },
    { to: "/campus/app/fortbildungen",     label: "Fortbildungs-Tracker", icon: GraduationCap },
    { to: "/campus/app/fortbildungsplan",  label: "Fortbildungsplan",     icon: GraduationCap },
  ],
};

// PDL-Suite-Gruppe: sichtbar für PDL-Tarife + Admins
const GROUP_PDL = {
  label: "PDL-Suite",
  items: [
    { to: "/campus/app/admin/qualitaets-dashboard", label: "Qualitäts-Dashboard", icon: BarChart3 },
  ],
};

// Admin-Gruppe: aufgeteilt in Pflege-KI-Tools + Verwaltung
const GROUP_ADMIN_KI = {
  label: "Admin · Pflege-KI",
  items: [
    { to: "/campus/app/pflege-ki/admin",      label: "Pflege-KI · Admin",     icon: BrainCircuit },
    { to: "/campus/app/pflege-ki/lernsystem", label: "Pflege-KI · Lernfälle", icon: BrainCircuit },
    { to: "/campus/app/admin/ki-nutzung",     label: "KI-Nutzung & Kosten",   icon: BarChart3 },
  ],
};

const GROUP_ADMIN_OPS = {
  label: "Admin · Operations",
  items: [
    { to: "/campus/app/admin/users",        label: "User-Verwaltung",  icon: Users },
    { to: "/campus/app/admin/user-import",  label: "User-Importer",   icon: Inbox },
    { to: "/campus/app/admin/outreach",     label: "Outreach · Pflegedienste", icon: Mail },
    { to: "/campus/app/admin/social-image", label: "LinkedIn-Bilder", icon: Sparkles },
    { to: "/campus/app/leads-inbox",        label: "Leads-Inbox",     icon: Inbox },
    { to: "/campus/app/admin/lead-statistik", label: "Lead-Statistik", icon: BarChart3 },
    { to: "/campus/app/buchungen",          label: "Buchungen",       icon: ClipboardList },
    { to: "/campus/app/reminder-stats",     label: "Reminder-Stats",  icon: BarChart3 },
    { to: "/campus/app/bundesland-stats",   label: "Bundesland-Stats", icon: MapPin },
  ],
};

const PDL_SUITE_TIERS = new Set(["PDL Suite", "PDL Suite Solo", "PDL Suite Team"]);

export default function CampusLayout() {
  const { user, logout, refresh } = useCampusAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Show success toast after returning from Stripe checkout
  useEffect(() => {
    if (params.get("subscription") === "success") {
      toast.success("Willkommen im Dickies-Campus! Dein Abo wird gleich aktiviert …");
      // re-fetch /me so the new tier shows up
      refresh && refresh();
      // clean the URL
      const next = new URLSearchParams(params);
      next.delete("subscription");
      next.delete("session_id");
      setParams(next, { replace: true });
    }
  }, [params, refresh, setParams]);

  // Sperre Body-Scroll wenn Mobile-Drawer offen
  useEffect(() => {
    if (mobileOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = original; };
    }
  }, [mobileOpen]);

  const isAdmin = !!user?.is_admin;
  const isPdl = PDL_SUITE_TIERS.has(user?.tier);
  const allGroups = [
    GROUP_HAUPT,
    GROUP_INHALTE,
    GROUP_KARRIERE,
    ...(isPdl || isAdmin ? [GROUP_PDL] : []),
    ...(isAdmin ? [GROUP_ADMIN_KI, GROUP_ADMIN_OPS] : []),
  ];

  // Bottom-Tabs auf Mobile: 4 wichtigste Ziele (immer griffbereit)
  const bottomTabs = [
    { to: "/campus/app",                icon: LayoutDashboard, label: "Start",   end: true },
    { to: "/campus/app/pflege-ki",      icon: BrainCircuit,    label: "KI" },
    { to: "/campus/app/patientenmappe", icon: FolderKanban,    label: "Mappen" },
    { to: "/campus/app/profil",         icon: User,            label: "Profil" },
  ];

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      {/* SIDEBAR – fixed left, Notion-style */}
      <aside
        className="hidden lg:flex fixed inset-y-0 left-0 w-[260px] flex-col border-r border-cream-300 bg-white"
        data-testid="campus-sidebar"
      >
        {/* Brand */}
        <div className="px-6 pt-7 pb-6">
          <Link to="/" className="block">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-sand-500">
              Dickies-Campus
            </div>
            <div className="mt-1 font-display text-[20px] font-semibold text-sand-800 leading-tight">
              Dickies-Campus
            </div>
          </Link>
        </div>

        {/* Nav · gruppiert mit Section-Headern · scrollbar bei Overflow */}
        <nav className="flex-1 px-3 overflow-y-auto min-h-0">
          {(() => {
            const isAdmin = !!user?.is_admin;
            const isPdl   = PDL_SUITE_TIERS.has(user?.tier);
            const groups = [
              GROUP_HAUPT,
              GROUP_INHALTE,
              GROUP_KARRIERE,
              ...(isPdl || isAdmin ? [GROUP_PDL] : []),
              ...(isAdmin ? [GROUP_ADMIN_KI, GROUP_ADMIN_OPS] : []),
            ];
            return groups;
          })().map((group) => (
            <div key={group.label} className="mb-4 last:mb-2">
              <div className="px-3 mt-2 mb-1 text-[10px] uppercase tracking-[0.18em] font-bold text-sand-400">
                {group.label}
              </div>
              {group.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`campus-nav-${n.label.toLowerCase().replace(/[#·\s]/g, "-")}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2 rounded-lg text-[13.5px] font-medium transition-colors mb-0.5 ${
                      isActive
                        ? "bg-cream-100 text-sand-800"
                        : "text-sand-600 hover:bg-cream-50 hover:text-sand-800"
                    }`
                  }
                >
                  <n.icon
                    className="h-[16px] w-[16px] shrink-0 opacity-80 group-hover:opacity-100"
                    strokeWidth={1.6}
                  />
                  {n.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User card + logout */}
        <div className="border-t border-cream-300 p-3">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg">
            <div className="h-9 w-9 shrink-0 rounded-full bg-terra/15 text-terra grid place-items-center font-semibold text-[13px]">
              {user?.name?.split(" ").map((p) => p[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-sand-800 truncate">{user?.name}</div>
              <div className="text-[11px] text-sand-500 truncate">{user?.tier}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="campus-logout"
            className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium text-sand-600 hover:bg-cream-50 hover:text-sand-800 transition-colors"
          >
            <LogOut className="h-[16px] w-[16px]" strokeWidth={1.6} />
            Abmelden
          </button>
        </div>
      </aside>

      {/* MOBILE TOP BAR — Hamburger + Brand */}
      <header className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-cream-300 px-4 h-14 flex items-center justify-between">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-1 -ml-1 text-sand-800"
          data-testid="mobile-menu-open"
          aria-label="Menü öffnen"
        >
          <Menu className="h-6 w-6" strokeWidth={1.8} />
        </button>
        <Link to="/campus/app" className="text-[13px] font-semibold text-sand-800">
          Dickies-Campus
        </Link>
        <button
          onClick={logout}
          className="p-1 -mr-1 text-sand-600"
          data-testid="mobile-logout"
          aria-label="Abmelden"
        >
          <LogOut className="h-5 w-5" strokeWidth={1.6} />
        </button>
      </header>

      {/* MOBILE DRAWER · Overlay + Slide-Panel mit voller Navigation */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50"
          data-testid="mobile-drawer"
        >
          <div
            className="absolute inset-0 bg-espresso-deep/60"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-[86%] max-w-[320px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-left duration-200">
            {/* Header */}
            <div className="px-5 pt-5 pb-4 flex items-start justify-between border-b border-cream-300">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-sand-500">
                  Dickies-Campus
                </div>
                <div className="mt-1 font-display text-[19px] font-semibold text-sand-800 leading-tight">
                  Dickies-Campus
                </div>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1 -mr-1 text-sand-600"
                data-testid="mobile-menu-close"
                aria-label="Schließen"
              >
                <X className="h-5 w-5" strokeWidth={1.6} />
              </button>
            </div>

            {/* Nav */}
            <nav className="flex-1 px-3 py-3 overflow-y-auto min-h-0">
              {allGroups.map((group) => (
                <div key={group.label} className="mb-4 last:mb-2">
                  <div className="px-3 mt-2 mb-1 text-[10px] uppercase tracking-[0.18em] font-bold text-sand-400">
                    {group.label}
                  </div>
                  {group.items.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[#·\s]/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-3 rounded-lg text-[14px] font-medium transition-colors mb-0.5 ${
                          isActive
                            ? "bg-cream-100 text-sand-800"
                            : "text-sand-600 hover:bg-cream-50 hover:text-sand-800"
                        }`
                      }
                    >
                      <n.icon className="h-[18px] w-[18px] shrink-0 opacity-80" strokeWidth={1.6} />
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>

            {/* User-Card + Logout */}
            <div className="border-t border-cream-300 p-3">
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg">
                <div className="h-9 w-9 shrink-0 rounded-full bg-terra/15 text-terra grid place-items-center font-semibold text-[13px]">
                  {user?.name?.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-sand-800 truncate">{user?.name}</div>
                  <div className="text-[11px] text-sand-500 truncate">{user?.tier}</div>
                </div>
              </div>
              <button
                onClick={() => { setMobileOpen(false); logout(); }}
                className="mt-1 w-full flex items-center gap-3 px-3 py-3 rounded-lg text-[13.5px] font-medium text-sand-600 hover:bg-cream-50 hover:text-sand-800 transition-colors"
              >
                <LogOut className="h-[18px] w-[18px]" strokeWidth={1.6} />
                Abmelden
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* MAIN */}
      <main className="lg:pl-[260px]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-6 lg:py-14 pb-24 lg:pb-14">
          <Outlet />
        </div>

        {/* Footer */}
        <div className="lg:pl-0 max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 pb-24 lg:pb-10">
          <div className="rounded-xl bg-cream-100 border border-cream-300 px-4 py-3 text-[12px] text-sand-600 inline-flex items-center gap-2">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-brand-rose" />
            Dickies-Campus  ·{" "}
            <Link to="/" className="text-sand-800 underline-offset-4 hover:underline">
              zurück zur Startseite
              <ArrowUpRight className="inline h-3 w-3 ml-0.5" />
            </Link>
          </div>
        </div>
      </main>

      {/* BOTTOM-TAB-NAVIGATION · Nur Mobile · 4 Schnellzugriffe (iOS-Style) */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-20 bg-white/95 backdrop-blur border-t border-cream-300"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="mobile-bottom-tabs"
      >
        <div className="grid grid-cols-4">
          {bottomTabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`bottom-tab-${t.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10.5px] font-semibold transition-colors ${
                  isActive ? "text-brand-darkred" : "text-sand-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <t.icon
                    className={`h-[22px] w-[22px] ${isActive ? "" : "opacity-70"}`}
                    strokeWidth={isActive ? 2 : 1.6}
                  />
                  <span>{t.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Welcome-Wizard – rendert sich selbst nur für frische User */}
      <OnboardingWizard />
    </div>
  );
}
